import { useRef, useState, useCallback, useEffect, useMemo } from "react";
import SuperDocTemplateBuilder, { FieldMenu } from "@superdoc-dev/template-builder";
import CloseIcon from "@mui/icons-material/Close";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField } from "@mui/material";
import { toast } from "react-hot-toast";
import { axios_tenant } from "../../../../axios";
import { prepareDocxForSuperDoc } from "../utils/prepareDocxForSuperDoc";
import "superdoc/style.css";
import "@superdoc-dev/template-builder/field-types.css";
import "./DocxEditor.css";

const toFieldOption = (label) => ({ id: label.replace(/\s+/g, "_"), label });


// default again.
const FONT_FAMILY_OPTIONS = [
    { label: "Arial", key: "Arial, sans-serif" },
    { label: "Calibri", key: "Calibri, sans-serif" },
    { label: "Cambria", key: "Cambria, serif" },
    { label: "Courier New", key: "Courier New, monospace" },
    { label: "Georgia", key: "Georgia, serif" },
    { label: "Impact", key: "Impact, sans-serif" },
    { label: "Times New Roman", key: "Times New Roman, serif" }
];


const AUTOSCROLL_EDGE_PX = 60;

const AUTOSCROLL_MAX_SPEED = 18;


const HEADER_FOOTER_SELECTOR = [
    "[data-sd-headerfooter-kind]",
    ".sd-header-footer",
    ".superdoc-page-header",
    ".superdoc-page-footer"
].join(", ");



const publishSuperDocUser = () => {
    const email = localStorage.getItem("TENANT_USER_MAIL");
    if (!email) {
        delete globalThis.__SUPERDOC_USER__;
        return;
    }
    globalThis.__SUPERDOC_USER__ = {
        id: localStorage.getItem("TENANT_USER_ID") || email,
        name: email,
        email
    };
};

export default function MapFieldsStep({
    file,
    database,
    onClose,
    onSaved,
    handleModuleSubmit,
    originatingRequestId,
    requestId,

    onCommentsChange
}) {
    const [draggingFieldId, setDraggingFieldId] = useState(null);
    const [selectedFieldId, setSelectedFieldId] = useState(null);
    const [datasetFields, setDatasetFields] = useState([]);
    const [jurisdictions, setJurisdictions] = useState([]);
    const [activeJurisdiction, setActiveJurisdiction] = useState(null);
    const [fieldsLoading, setFieldsLoading] = useState(true);
    const [preparedFile, setPreparedFile] = useState(null);
    const [saveDialogOpen, setSaveDialogOpen] = useState(false);
    const [formName, setFormName] = useState("");
    const [formNameError, setFormNameError] = useState("");
    const [saving, setSaving] = useState(false);
    const [checkingName, setCheckingName] = useState(false);
    const [nameAvailable, setNameAvailable] = useState(true);
    const [hasComments, setHasComments] = useState(false);
    const nameCheckSeqRef = useRef(0);
    const templateRef = useRef(null);
    const documentAreaRef = useRef(null);

    const isMountedRef = useRef(true);
    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);


    // Bibliographic fields available to insert are dataset-specific and come
    // straight from utils/customFormFields.js in patent-asistant-apis, so this
    // list can never drift from what generateCustomForm can actually fill in.
    // They arrive grouped by jurisdiction (US/AU/EP/Global for Patent, US/AU
    // for Trademark) for the sidebar filter; a field shared across
    // jurisdictions carries the same number in each, and generation fills it
    // whichever jurisdiction's label was placed.
    //
    // SuperDocTemplateBuilder only reads `fields.available` at mount time - it
    // doesn't re-sync when the prop changes later (that's why the sidebar list,
    // a plain React render, updates fine once the fetch resolves, but SuperDoc's
    // own "{{" trigger menu stays stuck on whatever it saw on first mount). So
    // the fetch has to finish, and datasetFields has to be populated, BEFORE
    // <SuperDocTemplateBuilder> is ever rendered - see fieldsLoading below.
    useEffect(() => {
        setFieldsLoading(true);
        if (!database) {
            setDatasetFields([]);
            setJurisdictions([]);
            setActiveJurisdiction(null);
            setFieldsLoading(false);
            return;
        }
        let cancelled = false;
        axios_tenant
            .get(`/trexo-assistant/customForms/fields/${database}`)
            .then(({ data }) => {
                if (cancelled) return;
                const nextJurisdictions = (data?.jurisdictions || []).map((jurisdiction) => ({
                    ...jurisdiction,
                    fields: (jurisdiction.fields || []).map((field) => toFieldOption(field.label))
                }));
                setJurisdictions(nextJurisdictions);
                setActiveJurisdiction(nextJurisdictions[0]?.code ?? null);
                setDatasetFields((data?.fields || []).map(toFieldOption));
            })
            .catch(() => {
                if (!cancelled) {
                    toast.error("Couldn't load fields for the selected dataset.");
                    setDatasetFields([]);
                    setJurisdictions([]);
                    setActiveJurisdiction(null);
                }
            })
            .finally(() => {
                if (!cancelled) setFieldsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [database]);

    // The sidebar lists the selected jurisdiction's fields; SuperDoc's own
    // "{{" menu keeps the full datasetFields list.
    const visibleFields = useMemo(() => {
        if (jurisdictions.length === 0) return datasetFields;
        return jurisdictions.find((jurisdiction) => jurisdiction.code === activeJurisdiction)?.fields || [];
    }, [jurisdictions, activeJurisdiction, datasetFields]);

    // SuperDocTemplateBuilder only reads `document.source` at mount time
    // (same caveat as `fields.available` above), so the docx repairs have to
    // finish and preparedFile has to be set BEFORE it's ever rendered -
    // see fieldsLoading's gating below, which this joins.
    useEffect(() => {
        let cancelled = false;
        setPreparedFile(null);
        prepareDocxForSuperDoc(file)

            .catch(() => file)
            .then((prepared) => {
                if (cancelled) return;

                publishSuperDocUser();
                setPreparedFile(prepared);
            });
        return () => {
            cancelled = true;
        };
    }, [file]);

    const openSaveDialog = () => {
        if (!templateRef.current) return;
        setFormName(file?.name?.replace(/\.docx?$/i, "") || "");
        setFormNameError("");
        setCheckingName(false);
        setNameAvailable(true);
        setSaveDialogOpen(true);
    };


    useEffect(() => {
        if (!saveDialogOpen) return;
        const trimmedName = formName.trim();
        if (!trimmedName) {
            setNameAvailable(true);
            setCheckingName(false);
            return;
        }

        const seq = ++nameCheckSeqRef.current;
        setCheckingName(true);
        const debounce = setTimeout(() => {
            axios_tenant
                .get("/trexo-assistant/customForms/checkName", { params: { formName: trimmedName } })
                .then(({ data }) => {
                    if (nameCheckSeqRef.current !== seq) return;
                    setNameAvailable(!!data?.available);
                    setFormNameError(data?.available ? "" : (data?.message || "This name is already in use."));
                })
                .catch(() => {

                    if (nameCheckSeqRef.current === seq) setNameAvailable(true);
                })
                .finally(() => {
                    if (nameCheckSeqRef.current === seq) setCheckingName(false);
                });
        }, 400);

        return () => clearTimeout(debounce);
    }, [formName, saveDialogOpen]);

    const handleConfirmSave = async () => {
        const trimmedName = formName.trim();
        if (!trimmedName) {
            setFormNameError("Custom Form Name is required.");
            return;
        }
        if (!nameAvailable) {
            return;
        }

        setSaving(true);
        try {
            const blob = await templateRef.current.exportTemplate({
                fileName: trimmedName,
                triggerDownload: false
            });
            if (!(blob instanceof Blob)) {
                throw new Error("Could not export the mapped template.");
            }

            const mergeFieldFile = new File([blob], `${trimmedName}.docx`, {
                type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            });


            const largeSlotData = JSON.stringify({ formName: trimmedName, database });

            await handleModuleSubmit({
                textInput: "Custom Form Submitted",
                largeSlotData,
                originatingRequestId,
                requestId,
                usFilingFullData: { uploadedFiles: [mergeFieldFile] }
            });


            onSaved?.();
            return;
        } catch (err) {
            toast.error(err?.message || "Failed to save the Custom Form. Please try again.");
        }
        if (isMountedRef.current) setSaving(false);
    };


    const commentsUnsubscribeRef = useRef(null);

    const handleTemplateBuilderReady = useCallback(() => {
        const superdoc = templateRef.current?.getSuperDoc?.();
        if (!superdoc) return;

        const syncHasComments = () => {
            if (!isMountedRef.current) return;
            const next = (superdoc.commentsStore?.commentsList?.length || 0) > 0;
            setHasComments(next);
            onCommentsChange?.(next);
        };
        syncHasComments();

        if (typeof superdoc.on === "function") {
            superdoc.on("comments-update", syncHasComments);
            commentsUnsubscribeRef.current = () => superdoc.off?.("comments-update", syncHasComments);
        }
    }, [onCommentsChange]);

    useEffect(() => {
        const onKeyDown = (event) => {
            if (event.key !== "Backspace" || event.ctrlKey || event.metaKey || event.altKey || event.isComposing) return;
            const view = templateRef.current?.getSuperDoc?.()?.activeEditor?.view;
            if (!view?.dom || !view.dom.contains(event.target)) return;

            const { selection } = view.state;
            if (!selection.empty) return;
            const { $from } = selection;

            let from = null;
            let to = null;
            for (let depth = $from.depth; depth > 0; depth--) {
                if ($from.node(depth).type.name === "structuredContent") {
                    from = $from.before(depth);
                    to = $from.after(depth);
                    break;
                }
            }
            if (from === null && $from.nodeBefore?.type.name === "structuredContent") {
                from = $from.pos - $from.nodeBefore.nodeSize;
                to = $from.pos;
            }
            if (from === null) return;

            event.preventDefault();
            event.stopPropagation();
            view.dispatch(view.state.tr.delete(from, to).scrollIntoView());
        };
        document.addEventListener("keydown", onKeyDown, true);
        return () => document.removeEventListener("keydown", onKeyDown, true);
    }, []);

    useEffect(() => {
        const onMouseDown = (event) => {
            if (event.button !== 0) return;
            const el = event.target?.closest?.(".superdoc-structured-content-inline[data-sdt-id]");
            if (!el) return;
            const rect = el.getBoundingClientRect();
            const hit = 16;
            if (event.clientX < rect.right - hit || event.clientX > rect.right + 4) return;
            if (event.clientY < rect.top - 8 || event.clientY > rect.top + hit) return;

            const view = templateRef.current?.getSuperDoc?.()?.activeEditor?.view;
            if (!view) return;
            const sdtId = el.getAttribute("data-sdt-id");
            let range = null;
            view.state.doc.descendants((node, pos) => {
                if (range) return false;
                if (node.type.name === "structuredContent" && String(node.attrs.id) === sdtId) {
                    range = [pos, pos + node.nodeSize];
                    return false;
                }
                return true;
            });
            if (!range) return;

            event.preventDefault();
            event.stopPropagation();
            view.dispatch(view.state.tr.delete(range[0], range[1]));
        };
        document.addEventListener("mousedown", onMouseDown, true);
        return () => document.removeEventListener("mousedown", onMouseDown, true);
    }, []);
    useEffect(() => {
        return () => {
            commentsUnsubscribeRef.current?.();
            commentsUnsubscribeRef.current = null;
        };
    }, []);


    const getProseMirrorView = () => {
        const superdoc = templateRef.current?.getSuperDoc?.();

        if (!superdoc) {
            return null;
        }

        const candidates = {
            "activeEditor.view": superdoc.activeEditor?.view,
            "editor.view": superdoc.editor?.view,
            "view": superdoc.view,
            "activeEditor.editor.view": superdoc.activeEditor?.editor?.view
        };

        return Object.values(candidates).find(Boolean) || null;
    };

    // ---------------------------------------------------------------------
    // Iframe-aware document/window/editable resolution
    // ---------------------------------------------------------------------

    // SuperDoc may render its editable surface inside an iframe. Anything
    // that touches `document` / `window` globals (caretRangeFromPoint,
    // getSelection, scroll containers, etc.) needs to operate on the
    // *editor's own* document/window in that case, not the top-level page's.
    // This walks documentAreaRef looking for an iframe; if found (and
    // same-origin, so we can reach into it), returns its document/window,
    // otherwise falls back to the top-level document/window.
    const getEditorContext = () => {
        const container = documentAreaRef.current;
        const iframe = container?.querySelector("iframe");

        if (iframe) {
            try {
                const doc = iframe.contentDocument;
                const win = iframe.contentWindow;
                if (doc && win) {
                    const editable = doc.querySelector('[contenteditable="true"]');
                    return { doc, win, editable, iframe };
                }
            } catch {
                // Cross-origin iframe - can't reach in. Fall through to the
                // top-level document as a last resort (will likely fail to
                // find the editable node, but at least won't throw).
            }
        }

        return {
            doc: document,
            win: window,
            editable: container?.querySelector('[contenteditable="true"]'),
            iframe: null
        };
    };


    const toEditorDocCoords = (x, y) => {
        const { iframe } = getEditorContext();
        if (!iframe) return { x, y };
        const frameRect = iframe.getBoundingClientRect();
        return { x: x - frameRect.left, y: y - frameRect.top };
    };


    const getRegionTargetAtPoint = (clientX, clientY) => {
        const { doc } = getEditorContext();
        const { x, y } = toEditorDocCoords(clientX, clientY);
        const target = doc.elementFromPoint?.(x, y) || null;
        return {
            target,
            region: target?.closest?.(HEADER_FOOTER_SELECTOR) || null,
            x,
            y
        };
    };

    // Whether the drop point lands in a header/footer region at all.
    const isHeaderFooterPoint = (clientX, clientY) =>
        Boolean(getRegionTargetAtPoint(clientX, clientY).region);


    const isHeaderFooterEditorActive = () => {
        const view = templateRef.current?.getSuperDoc?.()?.activeEditor?.view;
        return Boolean(view?.dom?.closest?.(HEADER_FOOTER_SELECTOR));
    };


    const focusRegionForPoint = (clientX, clientY) => {
        const { doc, win } = getEditorContext();
        const { target, region, x, y } = getRegionTargetAtPoint(clientX, clientY);

        if (region) {

            target?.dispatchEvent(
                new win.MouseEvent("dblclick", {
                    bubbles: true,
                    cancelable: true,
                    clientX: x,
                    clientY: y,
                    detail: 2,
                    view: win
                })
            );
            return;
        }

        if (!isHeaderFooterEditorActive()) return;

        const activeDom = templateRef.current?.getSuperDoc?.()?.activeEditor?.view?.dom;
        (activeDom ?? doc.body)?.dispatchEvent(
            new win.KeyboardEvent("keydown", {
                bubbles: true,
                cancelable: true,
                key: "Escape",
                code: "Escape",
                view: win
            })
        );
    };


    const findScrollableAncestor = (el, doc) => {
        let node = el;
        while (node && node !== doc.body && node !== doc.documentElement) {
            const style = (node.ownerDocument.defaultView || window).getComputedStyle(node);
            const overflowY = style.overflowY;
            const canScrollY = (overflowY === "auto" || overflowY === "scroll") && node.scrollHeight > node.clientHeight;
            if (canScrollY) return node;
            node = node.parentElement;
        }
        return doc.scrollingElement || doc.documentElement;
    };


    const toViewLocalCoords = (view, x, y) => {
        const viewDoc = view.dom.ownerDocument;
        if (viewDoc === document) {

            return { x, y };
        }

        const frameEl = viewDoc.defaultView?.frameElement;
        if (!frameEl) {

            return { x, y };
        }

        const frameRect = frameEl.getBoundingClientRect();

        return { x: x - frameRect.left, y: y - frameRect.top };
    };


    const moveEditorSelectionToPoint = (x, y) => {
        const view = getProseMirrorView();
        if (!view || typeof view.posAtCoords !== "function") {
            return false;
        }

        try {
            const local = toViewLocalCoords(view, x, y);
            let result = view.posAtCoords({ left: local.x, top: local.y });


            if (!result && view.dom) {
                const viewRect = view.dom.getBoundingClientRect();

                if (local.y > viewRect.bottom) {
                    // Dropped below the editor content -> set position to end of document
                    result = { pos: view.state.doc.content.size };
                } else if (local.y < viewRect.top) {
                    // Dropped above the editor content -> set position to start of document
                    result = { pos: 0 };
                } else {
                    // Dropped in side margin or padding on a line: clamp coordinates inside viewRect
                    const clampedX = Math.max(
                        viewRect.left + 10,
                        Math.min(viewRect.right - 10, local.x)
                    );
                    const clampedY = Math.max(
                        viewRect.top + 5,
                        Math.min(viewRect.bottom - 5, local.y)
                    );

                    result = view.posAtCoords({ left: clampedX, top: clampedY });

                    // If edge clamping still missed, scan inward horizontally toward center of line
                    if (!result) {
                        const centerX = (viewRect.left + viewRect.right) / 2;
                        const steps = 10;
                        for (let i = 1; i <= steps; i++) {
                            const testX = clampedX + (centerX - clampedX) * (i / steps);
                            result = view.posAtCoords({ left: testX, top: clampedY });
                            if (result) break;
                        }
                    }
                }
            }

            if (!result || typeof result.pos !== "number") {
                return false;
            }

            const { state } = view;
            const maxPos = state.doc.content.size;
            const clampPos = Math.max(0, Math.min(maxPos, result.pos));
            const $pos = state.doc.resolve(clampPos);

            let selection = null;
            const Ctor = state.selection?.constructor;
            if (Ctor && typeof Ctor.near === "function") {
                try { selection = Ctor.near($pos); } catch (_) {}
            }
            if (!selection && Ctor && typeof Ctor.create === "function") {
                try { selection = Ctor.create(state.doc, clampPos); } catch (_) {}
            }
            if (!selection) {
                selection = state.selection;
            }

            if (selection) {
                view.dispatch(state.tr.setSelection(selection));
                view.focus();
            }
            return true;
        } catch {
            return false;
        }
    };


    const moveCaretToPoint = (x, y) => {
        const { doc, win, editable, iframe } = getEditorContext();


        let localX = x;
        let localY = y;
        if (iframe) {
            const frameRect = iframe.getBoundingClientRect();
            localX = x - frameRect.left;
            localY = y - frameRect.top;
        }

        const range = doc.caretRangeFromPoint
            ? doc.caretRangeFromPoint(localX, localY)
            : (() => {
                  const pos = doc.caretPositionFromPoint?.(localX, localY);
                  if (!pos) return null;
                  const r = doc.createRange();
                  r.setStart(pos.offsetNode, pos.offset);
                  r.collapse(true);
                  return r;
              })();

        if (!range) {
            return false;
        }

        if (editable && !editable.contains(range.startContainer)) {
            return false;
        }

        const selection = win.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
        editable?.focus({ preventScroll: true });
        return true;
    };


    const stopAutoScroll = useCallback(() => {
        if (autoScrollRafRef.current) {
            cancelAnimationFrame(autoScrollRafRef.current);
            autoScrollRafRef.current = null;
        }
        autoScrollSpeedRef.current = 0;
        autoScrollElRef.current = null;
    }, []);


    const runAutoScrollLoop = useCallback(() => {
        const el = autoScrollElRef.current;
        const speed = autoScrollSpeedRef.current;
        if (el && speed !== 0) {
            const initialTop = el.scrollTop;
            el.scrollTop += speed;

            const win = el.ownerDocument?.defaultView || (typeof el.scrollBy === "function" ? el : null);
            if (win && (el.scrollTop === initialTop || el === el.ownerDocument?.documentElement || el === el.ownerDocument?.body)) {
                win.scrollBy(0, speed);
            }
        }
        autoScrollRafRef.current = requestAnimationFrame(runAutoScrollLoop);
    }, []);


    const updateAutoScroll = useCallback((clientX, clientY) => {
        const { doc, iframe, editable } = getEditorContext();
        const container = documentAreaRef.current;
        if (!container) return;

        let boundsRect = null;
        let scrollEl = null;

        if (iframe) {
            boundsRect = iframe.getBoundingClientRect();
            scrollEl = findScrollableAncestor(editable, doc) || doc.documentElement || doc.body;
        } else {
            scrollEl = findScrollableAncestor(editable || container, doc);
            if (scrollEl) boundsRect = scrollEl.getBoundingClientRect();
        }

        if (!scrollEl || !boundsRect) {
            stopAutoScroll();
            return;
        }

        const distFromTop = clientY - boundsRect.top;
        const distFromBottom = boundsRect.bottom - clientY;


        if (clientX < boundsRect.left - 40 || clientX > boundsRect.right + 40) {
            stopAutoScroll();
            return;
        }

        let speed = 0;
        if (distFromTop >= -20 && distFromTop < AUTOSCROLL_EDGE_PX) {
            const depth = 1 - Math.max(0, distFromTop) / AUTOSCROLL_EDGE_PX;
            speed = -AUTOSCROLL_MAX_SPEED * Math.max(0.25, depth);
        } else if (distFromBottom >= -20 && distFromBottom < AUTOSCROLL_EDGE_PX) {
            const depth = 1 - Math.max(0, distFromBottom) / AUTOSCROLL_EDGE_PX;
            speed = AUTOSCROLL_MAX_SPEED * Math.max(0.25, depth);
        }

        if (speed === 0) {
            stopAutoScroll();
            return;
        }

        autoScrollElRef.current = scrollEl;
        autoScrollSpeedRef.current = speed;

        if (!autoScrollRafRef.current) {
            autoScrollRafRef.current = requestAnimationFrame(runAutoScrollLoop);
        }
    }, [runAutoScrollLoop, stopAutoScroll]);


    useEffect(() => {
        const onWindowDragEnd = () => stopAutoScroll();
        window.addEventListener("dragend", onWindowDragEnd);
        window.addEventListener("drop", onWindowDragEnd);
        return () => {
            window.removeEventListener("dragend", onWindowDragEnd);
            window.removeEventListener("drop", onWindowDragEnd);
            stopAutoScroll();
        };
    }, [stopAutoScroll]);


    const CustomFieldMenu = (props) => {
        const { position, isVisible } = props;
        const [menuPos, setMenuPos] = useState(null);

        useEffect(() => {
            if (!isVisible) {
                setMenuPos(null);
                return;
            }

            let animationFrameId;
            let isLocked = false;

            const calculatePosition = () => {
                if (isLocked) return;

                let left = 0;
                let top = 0;


                const view = getProseMirrorView();
                if (view && typeof view.coordsAtPos === "function") {
                    try {
                        const from = view.state.selection.from;
                        const coords = view.coordsAtPos(from);
                        if (coords && coords.left > 20 && (coords.bottom || coords.top) > 20) {
                            left = coords.left;
                            top = (coords.bottom ?? coords.top) + 4;
                        }
                    } catch (e) {
                        // ignore and try fallback
                    }
                }


                if (!left || !top) {
                    try {
                        const sel = window.getSelection();
                        if (sel && sel.rangeCount > 0) {
                            const range = sel.getRangeAt(0);
                            const rect = range.getBoundingClientRect();
                            if (rect && rect.left > 20 && rect.bottom > 20) {
                                left = rect.left;
                                top = rect.bottom + 4;
                            }
                        }
                    } catch (e) {
                        // ignore and try fallback
                    }
                }


                if (!left || !top) {
                    if (position && typeof position.left === "number" && position.left > 20) {
                        left = position.left;
                        top = position.top;
                    }
                }


                if (!left || !top) {
                    const container = documentAreaRef.current;
                    if (container) {
                        const rect = container.getBoundingClientRect();
                        left = rect.left + 100;
                        top = rect.top + 150;
                    } else {
                        left = window.innerWidth / 2 - 140;
                        top = window.innerHeight / 3;
                    }
                }

                if (left > 20 && top > 20) {

                    const margin = 12;
                    const menuWidth = 280;
                    const menuHeight = 320;
                    const clampedLeft = Math.max(margin, Math.min(left, window.innerWidth - menuWidth - margin));
                    const clampedTop = Math.max(margin, Math.min(top, window.innerHeight - menuHeight - margin));

                    setMenuPos({ left: clampedLeft, top: clampedTop });
                    isLocked = true; 
                }
            };

            calculatePosition();
            if (!isLocked) {
                const tick = () => {
                    if (isLocked) return;
                    calculatePosition();
                    animationFrameId = requestAnimationFrame(tick);
                };
                animationFrameId = requestAnimationFrame(tick);
            }

            return () => {
                if (animationFrameId) cancelAnimationFrame(animationFrameId);
            };
        }, [isVisible, position]);

        if (!isVisible) return null;

        const adjustedPosition = menuPos || {
            left: position?.left || 100,
            top: position?.top || 100
        };

        return <FieldMenu {...props} position={adjustedPosition} />;
    };

    const FIELD_MIME = "application/x-superdoc-field";


    const insertFieldAtSelection = (field) => {
        if (!templateRef.current) return;
        const insertOptions = { alias: field.label, fieldType: field.fieldType };
        if (field.mode === "block") {
            templateRef.current.insertBlockField(insertOptions);
        } else {
            templateRef.current.insertField(insertOptions);
        }
    };


    const handleFieldClick = (field) => {
        if (selectedFieldId === field.id) {
            setSelectedFieldId(null);
            return;
        }
        insertFieldAtSelection(field);
        setSelectedFieldId(field.id);
    };

    const handleFieldDragStart = (event, field) => {
        setDraggingFieldId(field.id);
        event.dataTransfer.effectAllowed = "copy";
        const payload = JSON.stringify(field);
        event.dataTransfer.setData(FIELD_MIME, payload);
        // Fallback for browsers that strip custom MIME types
        event.dataTransfer.setData("text/plain", payload);
    };

    const handleFieldDragEnd = () => {
        setDraggingFieldId(null);
        stopAutoScroll();
    };

    const handleDocumentDragOver = (event) => {
        if (!draggingFieldId) return;
        event.preventDefault();
        event.stopPropagation();
        event.dataTransfer.dropEffect = "copy";
        updateAutoScroll(event.clientX, event.clientY);
    };

    const handleDocumentDragLeave = (event) => {

        if (!documentAreaRef.current?.contains(event.relatedTarget)) {
            stopAutoScroll();
        }
    };

    const handleDocumentDrop = useCallback(
        async (event) => {
            if (!draggingFieldId) return;
            event.preventDefault();
            event.stopPropagation();
            stopAutoScroll();

            let field = null;
            try {
                const raw =
                    event.dataTransfer.getData(FIELD_MIME) ||
                    event.dataTransfer.getData("text/plain");
                if (raw) field = JSON.parse(raw);
            } catch {
                // Malformed drag payload - fall through, field stays null
            }

            setDraggingFieldId(null);
            if (!field || !templateRef.current) return;


            const wantsHeaderFooter = isHeaderFooterPoint(event.clientX, event.clientY);
            try {
                focusRegionForPoint(event.clientX, event.clientY);
                await new Promise((resolve) =>
                    requestAnimationFrame(() => requestAnimationFrame(resolve))
                );
            } catch {
                // Dispatch itself threw - the check below still catches it.
            }


            if (wantsHeaderFooter !== isHeaderFooterEditorActive()) {
                toast.error(
                    wantsHeaderFooter
                        ? "Couldn't place the field there. Double-click into the header or footer first, then click the field."
                        : "Couldn't place the field there. Press Escape to leave the header or footer first, then try again."
                );
                return;
            }

            try {
                const placedViaView = moveEditorSelectionToPoint(
                    event.clientX,
                    event.clientY
                );
                if (!placedViaView) {
                    moveCaretToPoint(event.clientX, event.clientY);
                }
            } catch {
                // Placement failed - fall through to inserting at the active selection
            }

            await new Promise((resolve) =>
                requestAnimationFrame(() => requestAnimationFrame(resolve))
            );

            insertFieldAtSelection(field);
        },
        [draggingFieldId, stopAutoScroll]
    );

    return (
        <div className={"docx-editor-container" + (hasComments ? " has-comments" : "")} style={{ height: "100%" }}>
            <div className="docx-editor-header">
                <div>
                    <div className="docx-editor-title">Map Fields</div>
                    <div className="docx-fields-sidebar-hint" style={{ margin: "4px 0 0 0" }}>
                        Drag fields onto the form, or click a spot in the form then click a field to place it there
                    </div>
                </div>
                <div className="docx-editor-actions">
                    <button className="docx-btn-save" onClick={openSaveDialog}>
                        + Save Form
                    </button>
                    <IconButton size="small" onClick={onClose} sx={{ color: "#94A3B8" }}>
                        <CloseIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                </div>
            </div>

            <div className="docx-editor-body">
                <div className="docx-editor-workspace">
                    <aside className="docx-fields-sidebar">
                        <div className="docx-fields-sidebar-title">Dataset Fields</div>
                        {jurisdictions.length > 1 && (
                            <div className="docx-jurisdiction-filter" role="tablist" aria-label="Filter fields by jurisdiction">
                                {jurisdictions.map((jurisdiction) => (
                                    <button
                                        key={jurisdiction.code}
                                        type="button"
                                        role="tab"
                                        aria-selected={activeJurisdiction === jurisdiction.code}
                                        className={
                                            "docx-jurisdiction-chip" +
                                            (activeJurisdiction === jurisdiction.code ? " docx-jurisdiction-chip-active" : "")
                                        }
                                        onClick={() => setActiveJurisdiction(jurisdiction.code)}
                                    >
                                        {jurisdiction.label}
                                        <span className="docx-jurisdiction-chip-count">{jurisdiction.fields.length}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                        <p className="docx-fields-sidebar-hint docx-fields-sidebar-hint-box">
                            Select the dataset below to place it in form. Once selected it will be placed where you click in the form.
                        </p>
                        <ul className="docx-fields-list">
                            {visibleFields.map((field) => (
                                <li
                                    key={field.id}
                                    className={
                                        "docx-field-item" +
                                        (draggingFieldId === field.id ? " docx-field-item-dragging" : "") +
                                        (selectedFieldId === field.id ? " docx-field-item-selected" : "")
                                    }
                                    draggable
                                    onDragStart={(e) => handleFieldDragStart(e, field)}
                                    onDragEnd={handleFieldDragEnd}
                                    onClick={() => handleFieldClick(field)}
                                >
                                    <span className="docx-field-item-handle" aria-hidden="true">
                                        ⠿
                                    </span>
                                    <span className="docx-field-item-label">{field.label}</span>
                                </li>
                            ))}
                        </ul>
                    </aside>

                    <div
                        className={
                            "docx-editor-document-area" +
                            (draggingFieldId ? " docx-editor-document-area-drop-active" : "")
                        }
                        ref={documentAreaRef}
                        onDragOverCapture={handleDocumentDragOver}
                        onDragLeaveCapture={handleDocumentDragLeave}
                        onDropCapture={handleDocumentDrop}
                    >
                        {fieldsLoading || !preparedFile ? (
                            <div className="docx-fields-sidebar-hint" style={{ padding: "24px", textAlign: "center" }}>
                                Loading dataset fields…
                            </div>
                        ) : (
                            <SuperDocTemplateBuilder
                                ref={templateRef}
                                document={{ source: preparedFile, mode: "editing" }}
                                fields={{ available: datasetFields }}
                                menu={{ component: CustomFieldMenu }}
                                toolbar={{ fonts: FONT_FAMILY_OPTIONS }}
                                defaultLockMode="contentLocked"
                                documentHeight="100%"
                                onReady={handleTemplateBuilderReady}
                            />
                        )}
                    </div>
                </div>
            </div>

            <Dialog open={saveDialogOpen} onClose={() => !saving && setSaveDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 700, fontSize: "16px" }}>Save Custom Form</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        margin="dense"
                        label="Custom Form Name"
                        value={formName}
                        onChange={(e) => {
                            setFormName(e.target.value);
                            setFormNameError("");
                        }}
                        error={!!formNameError}
                        helperText={formNameError || (checkingName ? "Checking availability…" : "")}
                        disabled={saving}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setSaveDialogOpen(false)} disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleConfirmSave}
                        disabled={saving || checkingName || !formName.trim() || !nameAvailable}
                    >
                        {saving ? "Saving…" : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}